import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../axiosInstance';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

function MainDashboard({ user }) {
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date();
    if (dateStr.includes('T')) return new Date(dateStr);
    return new Date(dateStr + 'T12:00:00');
  };

  const makeLocalDate = (y, m, d, h = 12, min = 0, s = 0) =>
    new Date(y, m, d, h, min, s);

  const [data, setData] = useState({
    fees: [],
    payroll: [],
    students: [],
    teachers: [],
    accountsReceivable: []
  });

  // INDEPENDENT FILTERS for each section
  const [sessionFilters, setSessionFilters] = useState({
    teacher: '',
    student: '',
    parent: '',
    fromDate: '',
    toDate: ''
  });

  const [payrollFilters, setPayrollFilters] = useState({
    teacher: '',
    payrollMonth: ''
  });

  const [parentFilters, setParentFilters] = useState({
    parent: '',
    parentMonth: ''
  });

  // Row limits with localStorage persistence
  const [sessionRowLimit, setSessionRowLimit] = useState(() => {
    return parseInt(localStorage.getItem('sessionRowLimit')) || 10;
  });

  const [payrollRowLimit, setPayrollRowLimit] = useState(() => {
    return parseInt(localStorage.getItem('payrollRowLimit')) || 10;
  });

  const [ytdPayrollRowLimit, setYtdPayrollRowLimit] = useState(() => {
    return parseInt(localStorage.getItem('ytdPayrollRowLimit')) || 10;
  });

  const [parentRowLimit, setParentRowLimit] = useState(() => {
    return parseInt(localStorage.getItem('parentRowLimit')) || 10;
  });

  // Save row limits to localStorage
  useEffect(() => {
    localStorage.setItem('sessionRowLimit', sessionRowLimit);
  }, [sessionRowLimit]);

  useEffect(() => {
    localStorage.setItem('payrollRowLimit', payrollRowLimit);
  }, [payrollRowLimit]);

  useEffect(() => {
    localStorage.setItem('ytdPayrollRowLimit', ytdPayrollRowLimit);
  }, [ytdPayrollRowLimit]);

  useEffect(() => {
    localStorage.setItem('parentRowLimit', parentRowLimit);
  }, [parentRowLimit]);

  const [activePreset, setActivePreset] = useState('');
  const [activePayrollPreset, setActivePayrollPreset] = useState('');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const now = new Date();
  const [editingSession, setEditingSession] = useState(null);
  const [editHours, setEditHours] = useState('');
  const [editDate, setEditDate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const currentYear = now.getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [expandedParentRows, setExpandedParentRows] = useState({});

  /* ===============================
     UPDATE CURRENT TIME
  ================================ */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  /* ===============================
     FETCH DATA
  ================================ */
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        console.log('Fetching data for user:', user.name, 'role:', user.role);

        const fetchPromises = [
          axiosInstance.get('/logs'),
          axiosInstance.get('/students'),
          axiosInstance.get('/teachers'),
          axiosInstance.get('/payroll')
        ];

        if (user.role === 'admin') {
          fetchPromises.push(axiosInstance.get('/receivables'));
        }

        const results = await Promise.all(fetchPromises);

        const feesRes = results[0];
        const studentRes = results[1];
        const teacherRes = results[2];
        const payrollRes = results[3];
        const arRes = results[4];

        console.log('✅ Fees data received:', feesRes.data.length, 'sessions');

        setData({
          fees: feesRes.data,
          payroll: payrollRes.data,
          students: studentRes.data,
          teachers: teacherRes.data,
          accountsReceivable: arRes ? arRes.data : []
        });

        console.log('✅ Data state updated');
      } catch (err) {
        console.error('❌ API error:', err.response?.config?.url, err.response?.status);
        console.error('Full error:', err);
      }
    };

    fetchAllData();
  }, [user.name, user.role]);

  /* ===============================
     DEFAULT PAYROLL MONTH
  ================================ */
  useEffect(() => {
    const now = parseLocalDate();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setPayrollFilters(f => ({ ...f, payrollMonth: month }));
  }, []);

  /* ===============================
     HELPERS
  ================================ */
  const getStudentByName = (name) => data.students.find(s => s.name === name);
  const getTeacherByName = (name) => data.teachers.find(t => t.name === name);
  const accountsReceivable = data.accountsReceivable || [];

  const getStudentRateOnDate = (student, sessionDate) => {
    if (!student) return 0;

    const date = new Date(sessionDate);

    if (!student.rateHistory || student.rateHistory.length === 0) {
      return student.rate;
    }

    for (let i = student.rateHistory.length - 1; i >= 0; i--) {
      const historyEntry = student.rateHistory[i];
      const effectiveDate = new Date(historyEntry.effectiveDate);
      const endDate = historyEntry.endDate ? new Date(historyEntry.endDate) : new Date('2099-12-31');

      if (date >= effectiveDate && date <= endDate) {
        return historyEntry.rate;
      }
    }

    return student.rate;
  };

  const getTeacherSalaryOnDate = (teacher, sessionDate) => {
    if (!teacher) return 0;

    const date = new Date(sessionDate);

    if (!teacher.salaryHistory || teacher.salaryHistory.length === 0) {
      return teacher.salary;
    }

    for (let i = teacher.salaryHistory.length - 1; i >= 0; i--) {
      const historyEntry = teacher.salaryHistory[i];
      const effectiveDate = new Date(historyEntry.effectiveDate);
      const endDate = historyEntry.endDate ? new Date(historyEntry.endDate) : new Date('2099-12-31');

      if (date >= effectiveDate && date <= endDate) {
        return historyEntry.salary;
      }
    }

    return teacher.salary;
  };

  /* ===============================
     STATS
  ================================ */
  const activeStudents = data.students.filter(s => s.active).length;

  const totalRevenue = data.fees
    .filter(f => parseLocalDate(f.date).getFullYear() === selectedYear)
    .reduce((sum, f) => {
      const student = getStudentByName(f.student);
      const historicalRate = getStudentRateOnDate(student, f.date);
      return sum + (historicalRate * f.hours);
    }, 0);

  const availableYears = Array.from(
    new Set(
      data.fees.map(f => parseLocalDate(f.date).getFullYear())
        .concat(data.payroll.map(p => parseLocalDate(p.date).getFullYear()))
    )
  ).sort((a, b) => b - a);

  useEffect(() => {
    if (availableYears.length && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const totalPayrollByYear = data.payroll
    .filter(p => parseLocalDate(p.date).getFullYear() === selectedYear)
    .reduce((sum, p) => sum + p.totalPay, 0);

  const totalProfit = totalRevenue - totalPayrollByYear;

  // Calculate revenue, payroll, and profit by month
  const monthlyFinancials = useMemo(() => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const allMonths = months.map((monthName, idx) => {
      const monthFees = data.fees.filter(f => {
        const d = parseLocalDate(f.date);
        return d.getMonth() === idx && d.getFullYear() === selectedYear;
      });

      const monthRevenue = monthFees.reduce((sum, f) => {
        const student = getStudentByName(f.student);
        const historicalRate = getStudentRateOnDate(student, f.date);
        return sum + (historicalRate * f.hours);
      }, 0);

      const monthPayroll = monthFees.reduce((sum, f) => {
        const teacher = getTeacherByName(f.teacher);
        const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);
        return sum + (historicalSalary * f.hours);
      }, 0);

      const monthProfit = monthRevenue - monthPayroll;

      return {
        month: monthName,
        monthIndex: idx,
        revenue: monthRevenue,
        payroll: monthPayroll,
        profit: monthProfit
      };
    });

    // If viewing current year, only show months up to current month
    // If viewing past year, show all months
    if (selectedYear === currentYear) {
      const currentMonth = now.getMonth(); // 0-indexed
      return allMonths.filter(m => m.monthIndex <= currentMonth);
    }

    return allMonths;
  }, [data.fees, data.students, data.teachers, selectedYear, currentYear, now, getStudentByName, getTeacherByName]);

  /* ===============================
     FILTERED SESSIONS
  ================================ */
  const filteredSessions = useMemo(() => {
    console.log('🔍 Computing filteredSessions...');

    const result = data.fees.filter(log => {
      const matchTeacher = user.role === 'admin'
        ? (!sessionFilters.teacher || log.teacher === sessionFilters.teacher)
        : true;

      const matchStudent = !sessionFilters.student || log.student === sessionFilters.student;

      const student = getStudentByName(log.student);
      const matchParent = !sessionFilters.parent || student?.parent === sessionFilters.parent;

      const logDate = parseLocalDate(log.date);
      const matchFrom = !sessionFilters.fromDate || logDate >= parseLocalDate(sessionFilters.fromDate);
      const matchTo = !sessionFilters.toDate || logDate <= parseLocalDate(sessionFilters.toDate);

      return matchTeacher && matchStudent && matchParent && matchFrom && matchTo;
    });

    console.log('  ✅ filteredSessions count:', result.length);
    return result;
  }, [data.fees, data.students, sessionFilters, user.role, getStudentByName]);

  const sessionTotals = useMemo(() => {
    return filteredSessions.reduce((acc, log) => {
      const student = getStudentByName(log.student);
      const feePerHour = getStudentRateOnDate(student, log.date);
      const totalFees = feePerHour * log.hours;

      return {
        hours: acc.hours + log.hours,
        totalFees: acc.totalFees + totalFees
      };
    }, { hours: 0, totalFees: 0 });
  }, [filteredSessions, data.students, getStudentByName]);

  /* ===============================
     PAYROLL (DERIVED FROM FEES)
  ================================ */
  const filteredPayrollFees = data.fees.filter(f => {
    const d = parseLocalDate(f.date);

    if (payrollFilters.teacher && f.teacher !== payrollFilters.teacher) return false;

    if (payrollFilters.payrollMonth) {
      const [y, m] = payrollFilters.payrollMonth.split('-').map(Number);
      return d.getFullYear() === y && d.getMonth() + 1 === m;
    }

    return d.getFullYear() === selectedYear;
  });

  const payrollByTeacherDetailed = {};

  data.fees
    .filter(f => {
      const d = parseLocalDate(f.date);
      return d.getFullYear() === selectedYear;
    })
    .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date))
    .forEach(f => {
      const teacher = getTeacherByName(f.teacher);
      if (!teacher) return;

      const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);
      const amount = f.hours * historicalSalary;
      const sessionDate = parseLocalDate(f.date);
      const monthKey = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}`;

      if (!payrollByTeacherDetailed[f.teacher]) {
        payrollByTeacherDetailed[f.teacher] = {
          bills: [],
          payments: []
        };
      }

      payrollByTeacherDetailed[f.teacher].bills.push({
        date: sessionDate,
        monthKey,
        hours: f.hours,
        amount,
        paid: 0,
        remaining: amount
      });
    });

  data.payroll
    .filter(p => {
      const d = parseLocalDate(p.date);
      return d.getFullYear() === selectedYear;
    })
    .forEach(p => {
      if (payrollByTeacherDetailed[p.teacher]) {
        payrollByTeacherDetailed[p.teacher].payments.push({
          date: parseLocalDate(p.date),
          amount: p.totalPay
        });
      }
    });

  Object.keys(payrollByTeacherDetailed).forEach(teacher => {
    const { bills, payments } = payrollByTeacherDetailed[teacher];

    payments.sort((a, b) => a.date - b.date);

    payments.forEach(payment => {
      let paymentRemaining = payment.amount;

      for (let i = 0; i < bills.length && paymentRemaining > 0; i++) {
        const bill = bills[i];
        const unpaid = bill.remaining;

        if (unpaid > 0) {
          const paymentApplied = Math.min(paymentRemaining, unpaid);
          bill.paid += paymentApplied;
          bill.remaining -= paymentApplied;
          paymentRemaining -= paymentApplied;
        }
      }
    });
  });

  const payrollByTeacher = {};

  filteredPayrollFees.forEach(f => {
    const teacher = getTeacherByName(f.teacher);
    const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);

    if (!payrollByTeacher[f.teacher]) {
      payrollByTeacher[f.teacher] = { hours: 0, totalPay: 0, paid: 0 };
    }

    payrollByTeacher[f.teacher].hours += f.hours;
    payrollByTeacher[f.teacher].totalPay += f.hours * historicalSalary;
  });

  Object.keys(payrollByTeacher).forEach(teacher => {
    if (payrollByTeacherDetailed[teacher]) {
      const sessionDate = parseLocalDate(filteredPayrollFees.find(f => f.teacher === teacher)?.date);
      const monthKey = payrollFilters.payrollMonth ||
        `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}`;

      const paidForMonth = payrollByTeacherDetailed[teacher].bills
        .filter(bill => {
          if (payrollFilters.payrollMonth) {
            return bill.monthKey === monthKey;
          }
          return bill.date.getFullYear() === selectedYear;
        })
        .reduce((sum, bill) => sum + bill.paid, 0);

      payrollByTeacher[teacher].paid = paidForMonth;
    }
  });

  const filteredPayroll = Object.entries(payrollByTeacher).map(([teacher, vals]) => {
    const outstanding = Math.max(vals.totalPay - vals.paid, 0);

    return {
      _id: payrollFilters.payrollMonth ? `${teacher}-${payrollFilters.payrollMonth}` : teacher,
      teacher,
      hours: vals.hours,
      totalPay: vals.totalPay,
      paid: vals.paid,
      outstanding
    };
  });

  const totalPayroll = filteredPayroll.reduce((sum, p) => sum + p.outstanding, 0);

  const payrollTotals = filteredPayroll.reduce((acc, p) => ({
    hours: acc.hours + p.hours,
    totalPay: acc.totalPay + p.totalPay,
    paid: acc.paid + p.paid,
    outstanding: acc.outstanding + p.outstanding
  }), { hours: 0, totalPay: 0, paid: 0, outstanding: 0 });

  /* ===============================
     DATE PRESETS
  ================================ */
  const setThisMonth = () => {
    setSessionFilters({
      ...sessionFilters,
      fromDate: makeLocalDate(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
      toDate: makeLocalDate(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]
    });
    setActivePreset('thisMonth');
  };

  const setPreviousMonth = () => {
    setSessionFilters({
      ...sessionFilters,
      fromDate: makeLocalDate(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0],
      toDate: makeLocalDate(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0]
    });
    setActivePreset('previousMonth');
  };

  const setThisMonthPayroll = () => {
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setPayrollFilters({ ...payrollFilters, payrollMonth: month });
    setActivePayrollPreset('thisMonth');
  };

  const setPreviousMonthPayroll = () => {
    const month = `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`;
    setPayrollFilters({ ...payrollFilters, payrollMonth: month });
    setActivePayrollPreset('previousMonth');
  };

  /* ===============================
     EDIT SESSION HELPERS
  ================================ */
  const openEditSession = (session) => {
    setEditingSession(session);
    setEditHours(session.hours);
    setEditDate(session.date.split('T')[0]);
  };

  const closeEditSession = () => {
    setEditingSession(null);
    setEditHours('');
    setEditDate('');
  };

  const saveEditedSession = async () => {
    if (!editingSession) return;

    setSavingEdit(true);
    try {
      await axiosInstance.patch(`/logs/${editingSession._id}`, {
        hours: Number(editHours),
        date: editDate
      });

      setData(prev => ({
        ...prev,
        fees: prev.fees.map(f =>
          f._id === editingSession._id
            ? { ...f, hours: Number(editHours), date: editDate }
            : f
        )
      }));

      closeEditSession();
    } catch (err) {
      console.error('Session update failed', err);
      alert('Failed to update session');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    try {
      await axiosInstance.delete(`/logs/${id}`);
      setData(prev => ({ ...prev, fees: prev.fees.filter(f => f._id !== id) }));
    } catch (err) {
      alert('Failed to delete session');
      console.error(err);
    }
  };

  /* ===============================
     YEAR-TO-DATE PAYROLL
  ================================ */
  const ytdPayroll = {};

  Object.entries(payrollByTeacherDetailed).forEach(([teacher, data]) => {
    const totalHours = data.bills.reduce((sum, bill) => sum + bill.hours, 0);
    const totalPayable = data.bills.reduce((sum, bill) => sum + bill.amount, 0);
    const totalPaid = data.bills.reduce((sum, bill) => sum + bill.paid, 0);

    ytdPayroll[teacher] = {
      hours: totalHours,
      payable: totalPayable,
      paid: totalPaid
    };
  });

  const ytdPayrollTotals = Object.values(ytdPayroll).reduce((acc, vals) => ({
    hours: acc.hours + vals.hours,
    payable: acc.payable + vals.payable,
    paid: acc.paid + vals.paid,
    outstanding: acc.outstanding + Math.max(vals.payable - vals.paid, 0)
  }), { hours: 0, payable: 0, paid: 0, outstanding: 0 });

  /* ===============================
     PARENT PAYMENT TRACKER - FIFO
  ================================ */
  const parentAllTransactions = {};

  accountsReceivable.forEach(ar => {
    const d = parseLocalDate(ar.date);

    //if (parentFilters.parent && ar.parent !== parentFilters.parent) return;

    if (!parentAllTransactions[ar.parent]) {
      parentAllTransactions[ar.parent] = [];
    }

    parentAllTransactions[ar.parent].push({
      date: d,
      amount: ar.amount,
      monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      sessionId: ar.sessionId
    });
  });

  const parentMonthlyCumulative = {};

  Object.entries(parentAllTransactions).forEach(([parent, transactions]) => {
    transactions.sort((a, b) => a.date - b.date);

    // Pass 1: collect all bills
    const allBills = [];
    const payments = [];
    let billIdCounter = 0;

    transactions.forEach(txn => {
      if (txn.amount > 0) {
        allBills.push({
          id: billIdCounter++,
          monthKey: txn.monthKey,
          amountBilled: txn.amount,
          amountPaid: 0,
          amountOwed: txn.amount,
          sessionId: txn.sessionId
        });
      } else {
        payments.push({ amount: Math.abs(txn.amount), date: txn.date });
      }
    });

    // Pass 2: apply all payments FIFO across all bills
    payments.forEach(payment => {
      let remaining = payment.amount;
      for (let i = 0; i < allBills.length && remaining > 0; i++) {
        const bill = allBills[i];
        const applied = Math.min(remaining, bill.amountOwed);
        bill.amountPaid += applied;
        bill.amountOwed -= applied;
        remaining -= applied;
      }
    });

    /* const billsQueue = [];
    let billIdCounter = 0;

    transactions.forEach(txn => {
      const { monthKey, amount, sessionId } = txn;

      if (amount > 0) {
        billsQueue.push({
          id: billIdCounter++,
          monthKey,
          amountBilled: amount,
          amountPaid: 0,
          amountOwed: amount,
          sessionId
        });
      } else {
        let paymentRemaining = Math.abs(amount);

        for (let i = 0; i < billsQueue.length && paymentRemaining > 0; i++) {
          const bill = billsQueue[i];
          const paymentApplied = Math.min(paymentRemaining, bill.amountOwed);
          bill.amountPaid += paymentApplied;
          bill.amountOwed -= paymentApplied;
          paymentRemaining -= paymentApplied;
        }

        while (billsQueue.length > 0 && billsQueue[0].amountOwed === 0) {
          billsQueue.shift();
        }
      }
    });

    const allBills = [];
    const tempBillsQueue = [];
    let tempBillId = 0;

    transactions.forEach(txn => {
      const { monthKey, amount, sessionId } = txn;

      if (amount > 0) {
        const newBill = {
          id: tempBillId++,
          monthKey,
          amountBilled: amount,
          amountPaid: 0,
          amountOwed: amount,
          sessionId
        };
        tempBillsQueue.push(newBill);
        allBills.push(newBill);
      } else {
        let paymentRemaining = Math.abs(amount);

        for (let i = 0; i < tempBillsQueue.length && paymentRemaining > 0; i++) {
          const bill = tempBillsQueue[i];
          const paymentApplied = Math.min(paymentRemaining, bill.amountOwed);
          bill.amountPaid += paymentApplied;
          bill.amountOwed -= paymentApplied;
          paymentRemaining -= paymentApplied;
        }

        while (tempBillsQueue.length > 0 && tempBillsQueue[0].amountOwed === 0) {
          tempBillsQueue.shift();
        }
      }
    }); */

    const monthlyData = {};

    allBills.forEach(bill => {
      const [year] = bill.monthKey.split('-').map(Number);

      if (year !== selectedYear) return;

      if (!monthlyData[bill.monthKey]) {
        monthlyData[bill.monthKey] = {
          billed: 0,
          paid: 0,
          outstanding: 0,
          sessions: []
        };
      }

      monthlyData[bill.monthKey].billed += bill.amountBilled;
      monthlyData[bill.monthKey].paid += bill.amountPaid;
      monthlyData[bill.monthKey].outstanding += bill.amountOwed;

      // Track session details - use SESSION date not AR date
      if (bill.sessionId) {
        const session = data.fees.find(f => f._id === bill.sessionId);
        if (session) {
          const sessionDate = parseLocalDate(session.date);
          const sessionMonthKey = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}`;

          // Only add to this month if the session actually occurred in this month
          if (sessionMonthKey === bill.monthKey) {
            monthlyData[bill.monthKey].sessions.push({
              student: session.student,
              hours: session.hours,
              amount: bill.amountBilled,
              date: session.date
            });
          }
        }
      }
    });

    parentMonthlyCumulative[parent] = monthlyData;
  });

  const processMonthlyData = (parentMonthlyCumulativeData) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return months.map((m, idx) => {
      const monthKey = `${selectedYear}-${String(idx + 1).padStart(2, '0')}`;

      const monthLogs = data.fees.filter(f => {
        const d = parseLocalDate(f.date);
        return d.getMonth() === idx && d.getFullYear() === selectedYear;
      });

      const totalFees = monthLogs.reduce((sum, f) => {
        const student = getStudentByName(f.student);
        const historicalRate = getStudentRateOnDate(student, f.date);
        return sum + (historicalRate * f.hours);
      }, 0);

      const totalPayroll = monthLogs.reduce((sum, f) => {
        const teacher = getTeacherByName(f.teacher);
        const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);
        return sum + (historicalSalary * f.hours);
      }, 0);

      let feesPaidThisMonth = 0;
      const parentsThisMonth = new Set(
        monthLogs.map(f => getStudentByName(f.student)?.parent).filter(Boolean)
      );

      parentsThisMonth.forEach(parent => {
        if (parentMonthlyCumulativeData[parent] && parentMonthlyCumulativeData[parent][monthKey]) {
          feesPaidThisMonth += parentMonthlyCumulativeData[parent][monthKey].paid;
        }
      });

      const teacherBills = {};
      monthLogs.forEach(f => {
        const teacher = getTeacherByName(f.teacher);
        if (!teacher) return;

        const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);
        const amount = f.hours * historicalSalary;

        if (!teacherBills[f.teacher]) {
          teacherBills[f.teacher] = [];
        }

        teacherBills[f.teacher].push({
          monthKey,
          date: parseLocalDate(f.date),
          amount,
          paid: 0
        });
      });

      Object.keys(teacherBills).forEach(teacherName => {
        const allTeacherFees = data.fees
          .filter(f => {
            const d = parseLocalDate(f.date);
            return f.teacher === teacherName && d.getFullYear() === selectedYear;
          })
          .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

        const billsQueue = [];
        allTeacherFees.forEach(f => {
          const teacher = getTeacherByName(f.teacher);
          if (!teacher) return;

          const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);
          const feeMonthKey = `${parseLocalDate(f.date).getFullYear()}-${String(parseLocalDate(f.date).getMonth() + 1).padStart(2, '0')}`;

          billsQueue.push({
            monthKey: feeMonthKey,
            amount: f.hours * historicalSalary,
            paid: 0
          });
        });

        const payments = data.payroll
          .filter(p => {
            const d = parseLocalDate(p.date);
            return p.teacher === teacherName && d.getFullYear() === selectedYear;
          })
          .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

        payments.forEach(payment => {
          let paymentRemaining = payment.totalPay;

          for (let i = 0; i < billsQueue.length && paymentRemaining > 0; i++) {
            const bill = billsQueue[i];
            const unpaid = bill.amount - bill.paid;
            const paymentApplied = Math.min(paymentRemaining, unpaid);

            bill.paid += paymentApplied;
            paymentRemaining -= paymentApplied;
          }
        });

        const paidForThisMonth = billsQueue
          .filter(b => b.monthKey === monthKey)
          .reduce((sum, b) => sum + b.paid, 0);

        if (!isNaN(paidForThisMonth)) {
          if (teacherBills[teacherName]) {
            teacherBills[teacherName].forEach(bill => {
              bill.paid = paidForThisMonth / teacherBills[teacherName].length;
            });
          }
        }
      });

      const payrollPaidThisMonth = Object.values(teacherBills)
        .flat()
        .reduce((sum, bill) => sum + bill.paid, 0);

      return {
        name: m,
        feesTotal: Math.round(totalFees * 100) / 100,
        feesPaid: Math.round(feesPaidThisMonth * 100) / 100,
        feesUnpaid: Math.round(Math.max(totalFees - feesPaidThisMonth, 0) * 100) / 100,
        payrollTotal: Math.round(totalPayroll * 100) / 100,
        payrollPaid: Math.round(payrollPaidThisMonth * 100) / 100,
        payrollUnpaid: Math.round(Math.max(totalPayroll - payrollPaidThisMonth, 0) * 100) / 100
      };
    });
  };

  const chartData = user.role === 'admin' ? processMonthlyData(parentMonthlyCumulative) : [];

  const parentTrackerRows = Object.entries(parentMonthlyCumulative)
    .filter(([parent]) => !parentFilters.parent || parent === parentFilters.parent)  // ← filter HERE
    .flatMap(([parent, months]) =>
      Object.entries(months)
        .filter(([monthKey]) => {
          if (parentFilters.parentMonth) {
            return monthKey === parentFilters.parentMonth;
          }
          return true;
        })
        .map(([monthKey, vals]) => ({
          parent,
          monthKey,
          ...vals
        }))
    );

  const parentTrackerTotals = parentTrackerRows.reduce((acc, row) => ({
    billed: acc.billed + row.billed,
    paid: acc.paid + row.paid,
    outstanding: acc.outstanding + row.outstanding
  }), { billed: 0, paid: 0, outstanding: 0 });

  // Calculate outstanding balance by parent for conditional row highlighting
  const parentOutstandingMonths = {};
  Object.entries(parentMonthlyCumulative).forEach(([parent, months]) => {
    const outstandingMonths = Object.entries(months)
      .filter(([monthKey, vals]) => vals.outstanding > 0)
      .map(([monthKey]) => monthKey);
    parentOutstandingMonths[parent] = outstandingMonths;
  });

  const isDarkMode = document.body.classList.contains('dark-mode');

  const getParentRowClass = (parent) => {
    const outstandingMonths = parentOutstandingMonths[parent] || [];
    if (outstandingMonths.length === 0) return 'row-paid';
    if (outstandingMonths.length === 1) return 'row-warning';
    return 'row-overdue';
  };

  /* ===============================
     FORMAT DATE/TIME
  ================================ */
  const formatDateTime = (date) => {
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const toggleParentRow = (key) => {
    setExpandedParentRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /* ===============================
     RENDER
  ================================ */
  return (
    <div className="page-container">
      <header className="content-header" style={{ position: 'relative' }}>
        <div>
          <h2>
            {user.role === 'admin' ? 'Academy Overview' : 'Apollo Academy Dashboard'}
          </h2>
          <p>
            {user.role === 'admin'
              ? `Welcome back, ${user.name}. Here is what's happening at Apollo Academy.`
              : `Welcome back, ${user.name}.`}
          </p>
        </div>

        <div style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          fontSize: '0.9rem',
          color: '#666',
          textAlign: 'right'
        }}>
          {formatDateTime(currentDateTime)}
        </div>
      </header>

      {user.role === 'admin' && (
        <>
          <div className="session-filters" style={{ marginBottom: '1rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label>Year</label>
              <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <button
              className="submit-btn"
              onClick={() => setSelectedYear(currentYear)}
              style={{ marginLeft: 'auto' }}
            >
              Reset to Current Year
            </button>
          </div>

          <div className="stats-row" style={{ marginBottom: '20px' }}>
            <div className="stat-card">
              <label>Total Revenue ({selectedYear})</label>
              <p className="val positive">${totalRevenue.toLocaleString()}</p>
            </div>

            <div className="stat-card">
              <label>Payroll ({selectedYear})</label>
              <p className="val negative">${totalPayrollByYear.toLocaleString()}</p>
            </div>

            <div className="stat-card">
              <label>Total Profit ({selectedYear})</label>
              <p className={`val ${totalProfit >= 0 ? 'positive' : 'negative'}`}>
                ${totalProfit.toLocaleString()}
              </p>
            </div>

            <div className="stat-card">
              <label>Active Students ({selectedYear})</label>
              <p className="val">{activeStudents}</p>
            </div>

            <div className="stat-card">
              <label>Net Margin ({selectedYear})</label>
              <p className="val">
                {totalRevenue > 0
                  ? `${(((totalRevenue - totalPayrollByYear) / totalRevenue) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>
          </div>

          {/* Monthly Financials */}
          <div style={{ marginBottom: '40px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '15px', color: 'var(--primary)' }}>
              Monthly Breakdown ({selectedYear})
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '15px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '10px'
            }}>
              {monthlyFinancials.map((m) => (
                <div
                  key={m.month}
                  className="stat-card"
                  style={{
                    padding: '20px',
                    minHeight: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <label style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '10px' }}>
                    {m.month}
                  </label>
                  <div style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: '#666' }}>Revenue:</span>
                      <span style={{ fontWeight: 600, color: '#10b981' }}>
                        ${m.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: '#666' }}>Payroll:</span>
                      <span style={{ fontWeight: 600, color: '#ef4444' }}>
                        ${m.payroll.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '8px',
                      borderTop: '1px solid #e5e7eb',
                      marginTop: '8px'
                    }}>
                      <span style={{ fontWeight: 600 }}>Profit:</span>
                      <span style={{
                        fontWeight: 700,
                        color: m.profit >= 0 ? '#10b981' : '#ef4444'
                      }}>
                        ${m.profit.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-wrapper">
              <h3>Fees Overview (Monthly)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="feesPaid" fill="#10b981" name="Fees Paid" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="feesUnpaid" fill="#f59e0b" name="Fees Unpaid" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-wrapper">
              <h3>Payroll Overview (Monthly)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="payrollPaid" fill="#6366f1" name="Payroll Paid" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="payrollUnpaid" fill="#ef4444" name="Payroll Unpaid" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* RECENT SESSIONS */}
      <div className="recent-sessions">
        <h3>Recent Student Sessions</h3>

        <div className="session-filters">
          {user.role === 'admin' && (
            <select value={sessionFilters.teacher} onChange={e => setSessionFilters({ ...sessionFilters, teacher: e.target.value })}>
              <option value="">All Teachers</option>
              {[...new Set(data.fees.map(f => f.teacher))].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}

          <select value={sessionFilters.student} onChange={e => setSessionFilters({ ...sessionFilters, student: e.target.value })}>
            <option value="">All Students</option>
            {[...new Set(data.fees.map(f => f.student))].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select value={sessionFilters.parent} onChange={e => setSessionFilters({ ...sessionFilters, parent: e.target.value })}>
            <option value="">All Parents</option>
            {[...new Set(data.students.map(s => s.parent))].map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          <div className="date-range">
            <label>From</label>
            <input
              type="date"
              value={sessionFilters.fromDate}
              onChange={e => {
                setSessionFilters({ ...sessionFilters, fromDate: e.target.value });
                setActivePreset('');
              }}
            />
          </div>

          <div className="date-range">
            <label>To</label>
            <input
              type="date"
              value={sessionFilters.toDate}
              onChange={e => {
                setSessionFilters({ ...sessionFilters, toDate: e.target.value });
                setActivePreset('');
              }}
            />
          </div>

          <div className="preset-buttons">
            <button
              type="button"
              className={`add-btn ${activePreset === 'thisMonth' ? 'preset-active' : ''}`}
              onClick={setThisMonth}
              disabled={selectedYear !== now.getFullYear()}
            >
              This Month
            </button>

            <button
              type="button"
              className={`add-btn ${activePreset === 'previousMonth' ? 'preset-active' : ''}`}
              onClick={setPreviousMonth}
            >
              Previous Month
            </button>
          </div>

          <button
            className="submit-btn"
            onClick={() => {
              setSessionFilters({
                teacher: '',
                student: '',
                parent: '',
                fromDate: '',
                toDate: ''
              });
              setActivePreset('');
            }}
          >
            Clear
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            <label style={{ fontSize: '0.9rem' }}>Show:</label>
            <select value={sessionRowLimit} onChange={e => setSessionRowLimit(Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={999999}>All</option>
            </select>
          </div>
        </div>

        {filteredSessions.length === 0 ? (
          <p>No sessions found.</p>
        ) : (
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Student</th>
                {user.role === 'admin' && <th>Teacher</th>}
                <th>Hours</th>
                {user.role === 'admin' && <th>Fee per Hour</th>}
                {user.role === 'admin' && <th>Total Fees</th>}
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions
                .sort((a, b) => {
                  // Sort by date descending (most recent first)
                  const dateCompare = parseLocalDate(b.date) - parseLocalDate(a.date);
                  if (dateCompare !== 0) return dateCompare;

                  // Then sort alphabetically by student name
                  return a.student.localeCompare(b.student);
                })
                .slice(0, sessionRowLimit === 999999 ? filteredSessions.length : sessionRowLimit)
                .map(log => {
                  const student = getStudentByName(log.student);
                  const feePerHour = getStudentRateOnDate(student, log.date);
                  const totalFees = feePerHour * log.hours;

                  return (
                    <tr key={log._id}>
                      <td>{parseLocalDate(log.date).toLocaleDateString()}</td>
                      <td>{student?.name || log.student || 'Unknown'}</td>
                      {user.role === 'admin' && <td>{log.teacher}</td>}
                      <td>{log.hours}</td>
                      {user.role === 'admin' && <td>${feePerHour.toFixed(2)}</td>}
                      {user.role === 'admin' && <td>${totalFees.toFixed(2)}</td>}
                      <td>
                        <button className="submit-btn" onClick={() => openEditSession(log)}>
                          Modify
                        </button>
                      </td>
                      <td>
                        <button className="add-btn" onClick={() => handleDeleteSession(log._id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid #ccc' }}>
                <td colSpan={user.role === 'admin' ? 3 : 2}>
                  Total (Showing {Math.min(sessionRowLimit, filteredSessions.length)} of {filteredSessions.length})
                </td>
                <td>{sessionTotals.hours.toFixed(2)}</td>
                {user.role === 'admin' && <td></td>}
                {user.role === 'admin' && <td>${sessionTotals.totalFees.toFixed(2)}</td>}
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}

        {/* EDIT SESSION MODAL */}
        {editingSession && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Modify Session</h3>

              <div className="form-group">
                <label>Date</label>
                <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} />
              </div>

              <div className="form-group">
                <label>Hours</label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={editHours}
                  onChange={e => setEditHours(e.target.value)}
                />
              </div>

              <div className="modal-actions">
                <button className="submit-btn" onClick={saveEditedSession} disabled={savingEdit}>
                  Save
                </button>
                <button className="cancel-btn" onClick={closeEditSession}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PAYROLL TABLE */}
      {user.role === 'admin' && (
        <div className="recent-sessions">
          <h3>Payroll</h3>

          <div className="session-filters">
            <select value={payrollFilters.teacher} onChange={e => setPayrollFilters({ ...payrollFilters, teacher: e.target.value })}>
              <option value="">All Teachers</option>
              {[...new Set(data.fees.map(f => f.teacher))].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            <div className="date-range">
              <label>Month</label>
              <input
                type="month"
                value={payrollFilters.payrollMonth}
                onChange={e => {
                  setPayrollFilters({ ...payrollFilters, payrollMonth: e.target.value });
                  setActivePayrollPreset('');
                }}
              />
            </div>

            <div className="preset-buttons">
              <button
                type="button"
                className={`add-btn ${activePayrollPreset === 'thisMonth' ? 'preset-active' : ''}`}
                onClick={setThisMonthPayroll}
              >
                This Month
              </button>

              <button
                type="button"
                className={`add-btn ${activePayrollPreset === 'previousMonth' ? 'preset-active' : ''}`}
                onClick={setPreviousMonthPayroll}
              >
                Previous Month
              </button>
            </div>

            <button
              className="submit-btn"
              onClick={() => {
                const now = parseLocalDate();
                const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                setPayrollFilters({ teacher: '', payrollMonth: month });
                setActivePayrollPreset('');
              }}
            >
              Clear Filters
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
              <label style={{ fontSize: '0.9rem' }}>Show:</label>
              <select value={payrollRowLimit} onChange={e => setPayrollRowLimit(Number(e.target.value))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={999999}>All</option>
              </select>
            </div>
          </div>

          <p>Total Payroll: ${totalPayroll.toFixed(2)}</p>

          <table className="sessions-table">
            <thead>
              <tr>
                <th>Teacher</th>
                <th>Hours Worked</th>
                <th>Total Payroll</th>
                <th>Paid</th>
                <th>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayroll
                .sort((a, b) => a.teacher.localeCompare(b.teacher))
                .slice(0, payrollRowLimit === 999999 ? filteredPayroll.length : payrollRowLimit)
                .map(row => (
                  <tr
                    key={row._id}
                    className={row.outstanding === 0 ? 'row-payroll-paid' : ''}
                  >
                    <td>{row.teacher}</td>
                    <td>{row.hours.toFixed(2)}</td>
                    <td>${row.totalPay.toFixed(2)}</td>
                    <td>${row.paid.toFixed(2)}</td>
                    <td style={{
                      color: Math.round(row.outstanding * 100) === 0 ? 'green' : 'red',
                      fontWeight: 600
                    }}>
                      {Math.round(row.outstanding * 100) === 0 ? 'Paid' : `$${row.outstanding.toFixed(2)}`}
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid #ccc' }}>
                <td>Total (Showing {Math.min(payrollRowLimit, filteredPayroll.length)} of {filteredPayroll.length})</td>
                <td>{payrollTotals.hours.toFixed(2)}</td>
                <td>${payrollTotals.totalPay.toFixed(2)}</td>
                <td>${payrollTotals.paid.toFixed(2)}</td>
                <td style={{ color: payrollTotals.outstanding > 0 ? 'red' : 'green' }}>
                  ${payrollTotals.outstanding.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>

          <div className="note-banner">
            <strong>Note:</strong> All payroll payments must be processed through the AR/AP page to ensure proper tracking.
          </div>
        </div>
      )}

      {/* YEAR-TO-DATE PAYROLL */}
      {user.role === 'admin' && Object.keys(ytdPayroll).length > 0 && (
        <div className="recent-sessions">
          <h3>Year-to-Date Payroll</h3>

          <div className="session-filters">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
              <label style={{ fontSize: '0.9rem' }}>Show:</label>
              <select value={ytdPayrollRowLimit} onChange={e => setYtdPayrollRowLimit(Number(e.target.value))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={999999}>All</option>
              </select>
            </div>
          </div>

          <table className="sessions-table">
            <thead>
              <tr>
                <th>Teacher</th>
                <th>Hours</th>
                <th>Payable</th>
                <th>Paid</th>
                <th>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(ytdPayroll)
                .sort(([teacherA], [teacherB]) => teacherA.localeCompare(teacherB))
                .slice(0, ytdPayrollRowLimit === 999999 ? Object.keys(ytdPayroll).length : ytdPayrollRowLimit)
                .map(([teacher, vals]) => {
                  const outstanding = Math.max(vals.payable - vals.paid, 0);

                  return (
                    <tr
                      key={teacher}
                      className={outstanding === 0 ? 'row-payroll-paid' : ''}
                    >
                      <td>{teacher}</td>
                      <td>{vals.hours.toFixed(2)}</td>
                      <td>${vals.payable.toFixed(2)}</td>
                      <td>${vals.paid.toFixed(2)}</td>
                      <td style={{
                        color: Math.round(outstanding * 100) === 0 ? 'green' : 'red',
                        fontWeight: 600
                      }}>
                        {Math.round(outstanding * 100) === 0 ? 'Paid' : `$${outstanding.toFixed(2)}`}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid #ccc' }}>
                <td>Total (Showing {Math.min(ytdPayrollRowLimit, Object.keys(ytdPayroll).length)} of {Object.keys(ytdPayroll).length})</td>
                <td>{ytdPayrollTotals.hours.toFixed(2)}</td>
                <td>${ytdPayrollTotals.payable.toFixed(2)}</td>
                <td>${ytdPayrollTotals.paid.toFixed(2)}</td>
                <td style={{ color: ytdPayrollTotals.outstanding > 0 ? 'red' : 'green' }}>
                  ${ytdPayrollTotals.outstanding.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* PARENT PAYMENT TRACKER */}
      {user.role === 'admin' && (
        <div className="recent-sessions">
          <h3>Parent Payment Tracker</h3>

          <div className="session-filters">
            <select value={parentFilters.parent} onChange={e => setParentFilters({ ...parentFilters, parent: e.target.value })}>
              <option value="">All Parents</option>
              {[...new Set(data.students.map(s => s.parent))].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <div className="date-range">
              <label>Month (Optional)</label>
              <input
                type="month"
                value={parentFilters.parentMonth}
                onChange={e => setParentFilters({ ...parentFilters, parentMonth: e.target.value })}
              />
            </div>

            <button
              className="submit-btn"
              onClick={() => {
                setParentFilters({ parent: '', parentMonth: '' });
              }}
            >
              Clear Filters
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
              <label style={{ fontSize: '0.9rem' }}>Show:</label>
              <select value={parentRowLimit} onChange={e => setParentRowLimit(Number(e.target.value))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={999999}>All</option>
              </select>
            </div>
          </div>

          <div className="info-banner">
            <strong>Note:</strong> Change the selected year above to view payment data for different years.
          </div>

          {parentTrackerRows.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No payment data found for the selected filters.
            </p>
          ) : (
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Parent</th>
                  <th>Month</th>
                  <th>Billed</th>
                  <th>Paid</th>
                  <th>Outstanding</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {parentTrackerRows
                  .sort((a, b) => {
                    // Sort by parent name alphabetically first
                    const parentCompare = a.parent.localeCompare(b.parent);
                    if (parentCompare !== 0) return parentCompare;

                    // Then sort by month key descending (most recent first)
                    return b.monthKey.localeCompare(a.monthKey);
                  })
                  .slice(0, parentRowLimit === 999999 ? parentTrackerRows.length : parentRowLimit)
                  .map((row, idx) => {
                    const rowKey = `${row.parent}-${row.monthKey}-${idx}`;
                    const isExpanded = expandedParentRows[rowKey];

                    return (
                      <React.Fragment key={rowKey}>
                        <tr className={getParentRowClass(row.parent)}>
                          <td>{row.parent}</td>
                          <td>{row.monthKey}</td>
                          <td>${row.billed.toFixed(2)}</td>
                          <td style={{ color: '#059669', fontWeight: 600 }}>${row.paid.toFixed(2)}</td>
                          <td style={{
                            color: Math.round(row.outstanding * 100) === 0 ? '#059669' : '#dc2626',
                            fontWeight: 600
                          }}>
                            {Math.round(row.outstanding * 100) === 0 ? 'Paid' : `$${row.outstanding.toFixed(2)}`}
                          </td>
                          <td>
                            {row.sessions && row.sessions.length > 0 ? (
                              <button
                                className="submit-btn"
                                onClick={() => toggleParentRow(rowKey)}
                                style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                              >
                                {isExpanded ? '▼ Hide' : '▶ Show'}
                              </button>
                            ) : (
                              <span style={{ fontSize: '0.85rem', color: '#999' }}>No details</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && row.sessions && row.sessions.length > 0 && (
                          <tr>
                            <td colSpan="6" style={{ padding: '0', backgroundColor: '#f9fafb' }}>
                              <div style={{ padding: '1rem', margin: '0.5rem' }}>
                                <table style={{ width: '100%', fontSize: '0.9rem' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                      <th style={{ textAlign: 'left', padding: '8px' }}>Student</th>
                                      <th style={{ textAlign: 'left', padding: '8px' }}>Date</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Hours</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.sessions
                                      .sort((a, b) => {
                                        // Sort by date descending
                                        const dateCompare = new Date(b.date) - new Date(a.date);
                                        if (dateCompare !== 0) return dateCompare;

                                        // Then alphabetically by student
                                        return a.student.localeCompare(b.student);
                                      })
                                      .map((session, sIdx) => (
                                        <tr key={sIdx}>
                                          <td style={{ padding: '8px' }}>{session.student}</td>
                                          <td style={{ padding: '8px' }}>
                                            {parseLocalDate(session.date).toLocaleDateString()}
                                          </td>
                                          <td style={{ textAlign: 'right', padding: '8px' }}>
                                            {session.hours.toFixed(2)}
                                          </td>
                                          <td style={{ textAlign: 'right', padding: '8px' }}>
                                            ${session.amount.toFixed(2)}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid #ccc' }}>
                  <td colSpan="2">Total (Showing {Math.min(parentRowLimit, parentTrackerRows.length)} of {parentTrackerRows.length})</td>
                  <td>${parentTrackerTotals.billed.toFixed(2)}</td>
                  <td style={{ color: 'green' }}>${parentTrackerTotals.paid.toFixed(2)}</td>
                  <td style={{ color: parentTrackerTotals.outstanding > 0 ? 'red' : 'green' }}>
                    ${parentTrackerTotals.outstanding.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default MainDashboard;